import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { safeLog } from '@/lib/safeLog';
import { useState, useEffect, useMemo, useCallback } from 'react';

export interface UnreadRecall {
  id: string;
  product_name: string;
  brand: string;
  severity_score: number | null;
  risk_type: string;
  category_id: string;
  published_at: string;
  motive: string | null;
}

const fetchLastSeenAt = async (): Promise<string | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('last_seen_at')
    .eq('id', user.id)
    .maybeSingle();

  if (error) {
    safeLog.error('[UNREAD_RECALLS] Error fetching last_seen_at:', error);
    return null;
  }

  return data?.last_seen_at || null;
};

const fetchUnreadRecalls = async (lastSeenAt: string | null): Promise<UnreadRecall[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  // If no last_seen_at, use 24 hours ago as default
  const since = lastSeenAt || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('recalls')
    .select('id, product_name, brand, severity_score, risk_type, category_id, published_at, motive')
    .gt('published_at', since)
    .order('published_at', { ascending: false })
    .limit(50);

  if (error) {
    safeLog.error('[UNREAD_RECALLS] Error fetching recalls:', error);
    throw error;
  }

  return data || [];
};

export const useUnreadRecalls = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewRecall, setHasNewRecall] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch last_seen_at first
  const lastSeenQuery = useQuery({
    queryKey: ['last-seen-at'],
    queryFn: fetchLastSeenAt,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,
  });

  // Fetch unread recalls based on last_seen_at
  const recallsQuery = useQuery({
    queryKey: ['unread-recalls', lastSeenQuery.data],
    queryFn: () => fetchUnreadRecalls(lastSeenQuery.data ?? null),
    enabled: lastSeenQuery.isFetched,
    staleTime: 1 * 60 * 1000, // 1 minute - needs to be fresh
    gcTime: 15 * 60 * 1000,
  });

  // Count unread (all recalls since last_seen_at are considered unread)
  const unreadCount = useMemo(() => {
    return recallsQuery.data?.length || 0;
  }, [recallsQuery.data]);

  // Listen for auth changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.user) {
          queryClient.invalidateQueries({ queryKey: ['last-seen-at'] });
          queryClient.invalidateQueries({ queryKey: ['unread-recalls'] });
        } else {
          queryClient.setQueryData(['unread-recalls'], []);
          queryClient.setQueryData(['last-seen-at'], null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [queryClient]);

  // Listen for new recalls via realtime
  useEffect(() => {
    const channel = supabase
      .channel('recalls-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'recalls'
        },
        (payload) => {
          safeLog.debug('[UNREAD_RECALLS] New recall received:', payload.new);
          queryClient.invalidateQueries({ queryKey: ['unread-recalls'] });
          
          // Trigger bell animation for 3 seconds
          setHasNewRecall(true);
          setTimeout(() => setHasNewRecall(false), 3000);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Mark all as seen - updates last_seen_at to now
  const markAllAsSeen = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const now = new Date().toISOString();

    const { error } = await supabase
      .from('profiles')
      .update({ last_seen_at: now })
      .eq('id', user.id);

    if (error) {
      safeLog.error('[UNREAD_RECALLS] Error updating last_seen_at:', error);
      return;
    }

    // Update local cache
    queryClient.setQueryData(['last-seen-at'], now);
    queryClient.setQueryData(['unread-recalls'], []);
    
    toast({
      title: "Notifications marquées comme vues"
    });
  }, [queryClient, toast]);

  // Handle recall click - navigate to recall detail
  const handleRecallClick = useCallback((recall: UnreadRecall) => {
    setIsOpen(false);
    navigate(`/recall/${recall.id}`);
  }, [navigate]);

  // When opening the panel, optionally mark as seen
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  return {
    recalls: recallsQuery.data ?? [],
    unreadCount,
    hasNewRecall,
    isOpen,
    setIsOpen: handleOpenChange,
    loading: recallsQuery.isLoading || lastSeenQuery.isLoading,
    markAllAsSeen,
    handleRecallClick,
    refetch: recallsQuery.refetch
  };
};
